import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CreditCard,
  Download,
  FlaskConical,
  Gauge,
  Minus,
  Plus,
  ReceiptText,
  Sparkles,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Badge, type BadgeProps } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ErrorState } from '@/components/ui/error-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/components/ui/toast';
import { ApiError, openAuthedFile, request } from '@/lib/api';
import {
  formatMinor,
  monthlyPriceMinor,
  usePlanCatalog,
  type BillingInterval,
  type CatalogPlan,
  type PlanCatalog,
} from '@/lib/plan-catalog';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import {
  PLAN_ORDER,
  useBillingInvoices,
  useBillingOverview,
  useRefreshSessionBilling,
  type BillingOverview,
  type Selection,
  type Subscription,
  type SubscriptionStatus,
} from './billing-api';
import { PlanChangeDialog } from './plan-change-dialog';

/* ---------------------------------------------------------------------------
   הגדרות ← חיוב ומנוי.

   בעלים: הכל. מנהל: צפייה בלבד (canManage=false מהשרת) — בלי כפתורים.
   המחירים מהקטלוג בשרת; שמות התוכניות תמיד מ-i18n (billing:plans.<KEY>.name).
   --------------------------------------------------------------------------- */

const STATUS_TONE: Record<SubscriptionStatus, BadgeProps['tone']> = {
  TRIALING: 'info',
  ACTIVE: 'success',
  PAST_DUE: 'danger',
  CANCELED: 'neutral',
  EXPIRED: 'neutral',
};

/** checkout: אין מנוי בתשלום (ניסיון, פג, בוטל, מתנה). change: מנוי פעיל. */
function modeOf(sub: Subscription | null): 'checkout' | 'change' | 'blocked' {
  if (!sub) return 'checkout';
  if (sub.complimentary) return 'checkout';
  if (sub.status === 'ACTIVE') return 'change';
  if (sub.status === 'PAST_DUE') return 'blocked';
  return 'checkout';
}

export function BillingPanel() {
  const overview = useBillingOverview();
  const catalog = usePlanCatalog();
  const invoices = useBillingInvoices();

  if (overview.isLoading || catalog.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }
  if (overview.isError || !overview.data) {
    return (
      <Card>
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      </Card>
    );
  }
  const o = overview.data;

  return (
    <div className="space-y-5">
      <CurrentPlanCard overview={o} />
      <UsageCard overview={o} />
      {catalog.data ? <PlanPicker overview={o} catalog={catalog.data} /> : null}
      <InvoicesCard
        invoices={invoices.data}
        loading={invoices.isLoading}
        error={invoices.isError ? invoices.error : null}
      />
      {o.canManage && o.provider === 'simulated' && o.subscription ? <DemoTools /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// המנוי הנוכחי
// ---------------------------------------------------------------------------

function useBillingAction() {
  const qc = useQueryClient();
  const toast = useToast();
  const { t } = useTranslation('billing');
  const refreshSession = useRefreshSessionBilling();
  return async (fn: () => Promise<unknown>, success?: string) => {
    try {
      await fn();
      if (success) toast.success(success);
    } catch (e) {
      if (e instanceof ApiError && e.code === 'PAYMENT_FAILED')
        toast.error(t('dialog.paymentFailed'), t('dialog.paymentFailedBody'));
      else toast.error(t('panel.actionFailed'), e instanceof ApiError ? e.message : undefined);
    } finally {
      await qc.invalidateQueries({ queryKey: ['billing'] });
      await refreshSession();
    }
  };
}

function CurrentPlanCard({ overview }: { overview: BillingOverview }) {
  const { t, i18n } = useTranslation('billing');
  const sub = overview.subscription;
  const run = useBillingAction();
  const [busy, setBusy] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const money = (minor: number) => formatMinor(minor, i18n.language);
  const manage = overview.canManage;

  const act = (key: string, fn: () => Promise<unknown>, success?: string) => {
    setBusy(key);
    void run(fn, success).finally(() => setBusy(null));
  };

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-3">
        <div className="flex gap-3">
          <div className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-accent-subtle text-accent">
            <CreditCard className="size-4" aria-hidden />
          </div>
          <div>
            <CardTitle>{t('current.title')}</CardTitle>
            {sub ? (
              <CardDescription>
                {t('current.planLine', {
                  plan: t(`plans.${sub.plan}.name`),
                  interval: t(`interval.${sub.interval}`),
                })}
                {sub.extraSeats > 0
                  ? ` · ${t('current.extraSeats', { count: sub.extraSeats })}`
                  : ''}
              </CardDescription>
            ) : null}
          </div>
        </div>
        {sub ? <Badge tone={STATUS_TONE[sub.status]}>{t(`status.${sub.status}`)}</Badge> : null}
      </CardHeader>

      <CardContent className="space-y-3 text-sm">
        {!sub ? <p className="text-fg-muted">{t('current.noSubscription')}</p> : null}

        {sub?.status === 'TRIALING' ? (
          <div>
            <p className="font-semibold text-fg">
              {sub.trialDaysLeft === 0
                ? t('current.trialEndsToday')
                : t('current.trialDaysLeft', { count: sub.trialDaysLeft ?? 0 })}
            </p>
            <p className="text-xs text-fg-muted">
              {t('current.trialEnds', { date: formatDate(sub.trialEndsAt) })}
            </p>
          </div>
        ) : null}

        {sub?.status === 'ACTIVE' && !sub.cancelAtPeriodEnd && !sub.complimentary ? (
          <p className="text-fg">
            {overview.nextInvoice
              ? t('current.nextCharge', {
                  amount: money(overview.nextInvoice.totalMinor),
                  date: formatDate(overview.nextInvoice.date),
                })
              : t('current.renews', { date: formatDate(sub.currentPeriodEnd) })}
          </p>
        ) : null}

        {sub?.readOnly ? <Notice tone="warning">{t('current.readOnly')}</Notice> : null}

        {sub?.cancelAtPeriodEnd && sub.status === 'ACTIVE' ? (
          <Notice tone="warning">
            <p>{t('current.cancelNotice', { date: formatDate(sub.currentPeriodEnd) })}</p>
            {manage ? (
              <Button
                size="sm"
                className="mt-2"
                loading={busy === 'resume'}
                onClick={() =>
                  act(
                    'resume',
                    () => request('/billing/resume', { method: 'POST', body: {} }),
                    t('current.resumed'),
                  )
                }
              >
                {t('current.resume')}
              </Button>
            ) : null}
          </Notice>
        ) : null}

        {sub?.status === 'PAST_DUE' ? (
          <Notice tone="danger">
            <p className="font-semibold">{t('current.pastDueTitle')}</p>
            {sub.amountDueMinor !== null ? (
              <p>{t('current.pastDueAmount', { amount: money(sub.amountDueMinor) })}</p>
            ) : null}
            {sub.graceEndsAt ? (
              <p>{t('current.pastDueGrace', { date: formatDate(sub.graceEndsAt) })}</p>
            ) : null}
            {manage ? (
              <div className="mt-2 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  loading={busy === 'retry'}
                  onClick={() =>
                    act(
                      'retry',
                      () => request('/billing/retry-payment', { method: 'POST', body: {} }),
                      t('current.retrySuccess'),
                    )
                  }
                >
                  {t('current.retry')}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={busy === 'card'}
                  onClick={() =>
                    act('card', async () => {
                      const r = await request('/billing/payment-method', {
                        method: 'POST',
                        body: {},
                        schema: z.object({ url: z.string() }),
                      });
                      window.location.assign(r.url);
                    })
                  }
                >
                  {t('current.updateCard')}
                </Button>
              </div>
            ) : null}
          </Notice>
        ) : null}

        {sub?.pending ? (
          <Notice tone="info">
            <p>
              {t('current.pendingNotice', {
                date: formatDate(sub.pending.effectiveAt),
                plan: t(`plans.${sub.pending.plan}.name`),
                interval: t(`interval.${sub.pending.interval}`),
              })}
              {sub.pending.extraSeats > 0
                ? ` ${t('current.pendingSeats', { count: sub.pending.extraSeats })}`
                : ''}
            </p>
            {manage ? (
              <Button
                size="sm"
                variant="secondary"
                className="mt-2"
                loading={busy === 'clear'}
                onClick={() =>
                  act(
                    'clear',
                    () =>
                      request('/billing/change', {
                        method: 'POST',
                        body: {
                          plan: sub.plan,
                          interval: sub.interval,
                          extraSeats: sub.extraSeats,
                        },
                      }),
                    t('current.pendingCleared'),
                  )
                }
              >
                {t('current.cancelPending')}
              </Button>
            ) : null}
          </Notice>
        ) : null}

        {sub?.coupon ? (
          <p className="text-xs text-success">
            {t('current.coupon', { percent: sub.coupon.percent, count: sub.coupon.monthsLeft })}
          </p>
        ) : null}
        {sub?.complimentary ? (
          <p className="text-xs text-fg-muted">{t('current.complimentary')}</p>
        ) : null}
        {overview.provider === 'simulated' ? (
          <p className="text-xs text-fg-muted">{t('current.demo')}</p>
        ) : null}
        {!manage ? <p className="text-xs text-fg-muted">{t('panel.managerNote')}</p> : null}

        {manage && sub?.status === 'ACTIVE' && !sub.cancelAtPeriodEnd && !sub.complimentary ? (
          <div className="border-t border-border pt-3">
            <Button size="sm" variant="ghost" onClick={() => setCancelling(true)}>
              {t('current.cancel')}
            </Button>
          </div>
        ) : null}
      </CardContent>

      {cancelling && sub ? (
        <CancelDialog periodEnd={sub.currentPeriodEnd} onClose={() => setCancelling(false)} />
      ) : null}
    </Card>
  );
}

function Notice({ tone, children }: { tone: 'warning' | 'danger' | 'info'; children: ReactNode }) {
  const cls = {
    warning: 'border-warning-border bg-warning-subtle',
    danger: 'border-danger-border bg-danger-subtle',
    info: 'border-info-border bg-info-subtle',
  }[tone];
  return (
    <div className={cn('rounded-(--radius-md) border p-3 text-xs text-fg', cls)}>{children}</div>
  );
}

function CancelDialog({ periodEnd, onClose }: { periodEnd: string | null; onClose: () => void }) {
  const { t } = useTranslation('billing');
  const run = useBillingAction();
  const [busy, setBusy] = useState(false);
  const date = formatDate(periodEnd);
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent aria-describedby="cancel-sub-desc">
        <DialogHeader>
          <DialogTitle>{t('cancel.title')}</DialogTitle>
          <DialogDescription id="cancel-sub-desc">{t('cancel.until', { date })}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <ul className="list-disc space-y-1.5 ps-5 text-sm text-fg">
            <li>{t('cancel.readOnly')}</li>
            <li>{t('cancel.dataKept')}</li>
            <li>{t('cancel.resumeHint', { date })}</li>
          </ul>
        </DialogBody>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="secondary">
              {t('cancel.keep')}
            </Button>
          </DialogClose>
          <Button
            variant="danger"
            loading={busy}
            onClick={() => {
              setBusy(true);
              void run(
                () => request('/billing/cancel', { method: 'POST', body: {} }),
                t('cancel.done', { date }),
              ).finally(() => {
                setBusy(false);
                onClose();
              });
            }}
          >
            {t('cancel.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// שימוש
// ---------------------------------------------------------------------------

function Meter({ value, max }: { value: number; max: number | null }) {
  const pct = max && max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div
      className="h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
      role="progressbar"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max ?? undefined}
    >
      <div
        className={cn(
          'h-full rounded-full',
          pct >= 100 ? 'bg-danger' : pct >= 80 ? 'bg-warning' : 'bg-accent',
        )}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function UsageCard({ overview }: { overview: BillingOverview }) {
  const { t, i18n } = useTranslation('billing');
  const { seats, ai } = overview;
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-accent-subtle text-accent">
          <Gauge className="size-4" aria-hidden />
        </div>
        <CardTitle>{t('usage.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <div className="flex items-baseline justify-between text-sm">
            <span className="font-medium text-fg">{t('usage.seats')}</span>
            <span className="tabular-nums text-fg" data-testid="seats-value">
              {seats.limit !== null
                ? t('usage.seatsValue', { used: seats.used, limit: seats.limit })
                : t('usage.seatsUnlimited', { used: seats.used })}
            </span>
          </div>
          <Meter value={seats.used} max={seats.limit} />
          <ul className="grid gap-1 text-xs text-fg-muted sm:grid-cols-2">
            {seats.breakdown.map((b) => (
              <li key={b.role} className="flex justify-between gap-2">
                <span>{t(`roles.${b.role}`)}</span>
                <span className="tabular-nums">
                  {t('usage.roleLine', { count: b.count })}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-2xs text-fg-subtle">{t('usage.weightsHelp')}</p>
        </div>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between text-sm">
            <span className="flex items-center gap-1.5 font-medium text-fg">
              <Sparkles className="size-3.5 text-accent" aria-hidden />
              {t('usage.ai')}
            </span>
            <span className="tabular-nums text-fg">
              {t('usage.aiValue', {
                spent: formatMinor(ai.spentMinor, i18n.language),
                budget: formatMinor(ai.budgetMinor, i18n.language),
              })}
            </span>
          </div>
          <Meter value={ai.spentMinor} max={ai.budgetMinor} />
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// בחירת תוכנית
// ---------------------------------------------------------------------------

function PlanPicker({ overview, catalog }: { overview: BillingOverview; catalog: PlanCatalog }) {
  const { t } = useTranslation('billing');
  const sub = overview.subscription;
  const [interval, setBillingInterval] = useState<BillingInterval>(sub?.interval ?? 'MONTHLY');
  const [selected, setSelected] = useState<Selection | null>(null);
  const mode = modeOf(sub);
  const manage = overview.canManage && overview.paymentsAvailable && mode !== 'blocked';

  return (
    <Card>
      <CardHeader className="flex-row flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle>{t('picker.title')}</CardTitle>
          <CardDescription>{t('picker.description')}</CardDescription>
        </div>
        <div
          role="radiogroup"
          aria-label={t('picker.intervalLabel')}
          className="flex rounded-(--radius-md) border border-border bg-surface-sunken p-0.5 text-xs"
        >
          {(['MONTHLY', 'ANNUAL'] as const).map((iv) => (
            <button
              key={iv}
              type="button"
              role="radio"
              aria-checked={interval === iv}
              onClick={() => setBillingInterval(iv)}
              className={cn(
                'flex min-h-9 items-center gap-1.5 rounded-(--radius-sm) px-3 font-medium',
                interval === iv ? 'bg-surface-raised text-fg shadow-xs' : 'text-fg-muted',
              )}
            >
              {iv === 'MONTHLY' ? t('picker.monthly') : t('picker.annual')}
              {iv === 'ANNUAL' ? (
                <span className="rounded-full bg-success-subtle px-1.5 text-2xs text-success">
                  {t('picker.twoMonthsFree')}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {overview.canManage && !overview.paymentsAvailable ? (
          <Notice tone="warning">{t('panel.paymentsUnavailable')}</Notice>
        ) : null}
        {overview.canManage && mode === 'blocked' ? (
          <Notice tone="danger">{t('picker.pastDueBlocked')}</Notice>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          {catalog.plans.map((p) => (
            <PlanCard
              key={p.key}
              plan={p}
              catalog={catalog}
              interval={interval}
              sub={sub}
              mode={mode}
              canAct={manage}
              onPick={setSelected}
            />
          ))}
        </div>
        <p className="text-xs text-fg-muted">
          {t('picker.contact', { max: catalog.maxTotalSeats })}
        </p>
      </CardContent>
      {selected && mode !== 'blocked' ? (
        <PlanChangeDialog mode={mode} selection={selected} onClose={() => setSelected(null)} />
      ) : null}
    </Card>
  );
}

function PlanCard({
  plan,
  catalog,
  interval,
  sub,
  mode,
  canAct,
  onPick,
}: {
  plan: CatalogPlan;
  catalog: PlanCatalog;
  interval: BillingInterval;
  sub: Subscription | null;
  mode: 'checkout' | 'change' | 'blocked';
  canAct: boolean;
  onPick: (s: Selection) => void;
}) {
  const { t, i18n } = useTranslation('billing');
  const money = (minor: number) => formatMinor(minor, i18n.language);
  const seatsEnabled = plan.extraSeatsAllowed && plan.maxExtraSeats > 0;
  const [extra, setExtra] = useState(sub?.plan === plan.key ? sub.extraSeats : 0);
  const extraSeats = seatsEnabled ? extra : 0;

  const onPlan = sub?.plan === plan.key && sub.status !== 'EXPIRED' && sub.status !== 'CANCELED';
  const isCurrent =
    mode !== 'checkout' && onPlan && sub?.interval === interval && sub.extraSeats === extraSeats;
  const isTrialPlan = sub?.status === 'TRIALING' && sub.plan === plan.key;

  const label = (() => {
    if (mode === 'checkout') return t('picker.choose');
    if (!sub) return t('picker.choose');
    const rank = PLAN_ORDER.indexOf(plan.key) - PLAN_ORDER.indexOf(sub.plan);
    if (rank > 0) return t('picker.upgrade');
    if (rank < 0) return t('picker.downgrade');
    if (extraSeats > sub.extraSeats) return t('picker.upgrade');
    if (extraSeats < sub.extraSeats) return t('picker.downgrade');
    return t('picker.switch');
  })();

  const monthly = monthlyPriceMinor(catalog, plan.key, interval, extraSeats);
  const annualTotal =
    (plan.monthlyMinor + extraSeats * catalog.extraSeatMonthlyMinor) * catalog.annualMonthsCharged;

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-(--radius-lg) border p-4',
        isCurrent || isTrialPlan ? 'border-accent bg-accent-subtle/40' : 'border-border',
      )}
      data-testid={`plan-${plan.key}`}
    >
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-fg">{t(`plans.${plan.key}.name`)}</h4>
        {isCurrent ? (
          <Badge tone="accent">{t('picker.current')}</Badge>
        ) : isTrialPlan ? (
          <Badge tone="info">{t('picker.trialPlan')}</Badge>
        ) : null}
      </div>
      <div>
        <p className="text-xl font-bold text-fg tabular-nums">
          {money(monthly)}
          <span className="ms-1 text-xs font-normal text-fg-muted">
            {t('picker.perMonth')}{' '}
            {catalog.vatRateBps > 0 ? t('picker.plusVat') : t('picker.noVat')}
          </span>
        </p>
        {interval === 'ANNUAL' ? (
          <p className="text-2xs text-fg-muted">
            {t('picker.annualBilled', {
              amount: money(Math.round(annualTotal)),
            })}
          </p>
        ) : null}
      </div>
      <ul className="space-y-1 text-xs text-fg-muted">
        <li>{t('picker.seats', { count: plan.seats + extraSeats })}</li>
        <li>{t('picker.aiBudget', { amount: money(plan.aiBudgetMinor) })}</li>
      </ul>

      {seatsEnabled ? (
        <div className="space-y-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-fg">{t('picker.extraSeats')}</span>
            <div className="flex items-center gap-1">
              <Button
                size="icon"
                variant="secondary"
                className="size-8"
                aria-label={t('picker.decrease')}
                disabled={!canAct || extra <= 0}
                onClick={() => setExtra((v) => Math.max(0, v - 1))}
              >
                <Minus aria-hidden />
              </Button>
              <span className="w-8 text-center text-sm tabular-nums" aria-live="polite">
                {extra}
              </span>
              <Button
                size="icon"
                variant="secondary"
                className="size-8"
                aria-label={t('picker.increase')}
                disabled={!canAct || extra >= plan.maxExtraSeats}
                onClick={() => setExtra((v) => Math.min(plan.maxExtraSeats, v + 1))}
              >
                <Plus aria-hidden />
              </Button>
            </div>
          </div>
          <p className="text-2xs text-fg-subtle">
            {t('picker.extraSeatsHint', { price: money(catalog.extraSeatMonthlyMinor) })}
          </p>
        </div>
      ) : null}

      {canAct ? (
        <Button
          className="mt-auto"
          size="sm"
          variant={isCurrent ? 'secondary' : 'primary'}
          disabled={isCurrent}
          onClick={() => onPick({ plan: plan.key, interval, extraSeats })}
        >
          {isCurrent ? t('picker.current') : label}
        </Button>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// חשבוניות
// ---------------------------------------------------------------------------

const INVOICE_TONE: Record<string, BadgeProps['tone']> = {
  PAID: 'success',
  OPEN: 'info',
  VOID: 'neutral',
  FAILED: 'danger',
};

function InvoicesCard({
  invoices,
  loading,
  error,
}: {
  invoices: ReturnType<typeof useBillingInvoices>['data'];
  loading: boolean;
  error: unknown;
}) {
  const { t } = useTranslation('billing');
  const toast = useToast();
  return (
    <Card>
      <CardHeader className="flex-row items-center gap-3">
        <div className="grid size-9 shrink-0 place-items-center rounded-(--radius-md) bg-accent-subtle text-accent">
          <ReceiptText className="size-4" aria-hidden />
        </div>
        <CardTitle>{t('invoices.title')}</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        {loading ? (
          <Skeleton className="h-16 w-full" />
        ) : error ? (
          <ErrorState error={error} />
        ) : !invoices || invoices.length === 0 ? (
          <p className="text-sm text-fg-muted">{t('invoices.empty')}</p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border text-start text-fg-muted">
                <th className="py-2 text-start font-medium">{t('invoices.number')}</th>
                <th className="py-2 text-start font-medium">{t('invoices.date')}</th>
                <th className="py-2 text-start font-medium">{t('invoices.period')}</th>
                <th className="py-2 text-start font-medium">{t('invoices.amount')}</th>
                <th className="py-2 text-start font-medium">{t('invoices.status')}</th>
                <th className="py-2 text-start font-medium">
                  <span className="sr-only">{t('invoices.pdf')}</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {invoices.map((inv) => (
                <tr key={inv.id}>
                  <td className="py-2 pe-2">
                    <span className="ltr-inline tabular-nums">{inv.number}</span>
                    <span className="block text-2xs text-fg-subtle">
                      {t(`invoices.kinds.${inv.kind}`)}
                    </span>
                  </td>
                  <td className="py-2 pe-2 tabular-nums">{formatDate(inv.issuedAt)}</td>
                  <td className="py-2 pe-2 tabular-nums">
                    {formatDate(inv.periodStart)} – {formatDate(inv.periodEnd)}
                  </td>
                  <td className="py-2 pe-2 tabular-nums">{formatCurrency(inv.totalAmount)}</td>
                  <td className="py-2 pe-2">
                    <span className="flex flex-wrap gap-1">
                      <Badge tone={INVOICE_TONE[inv.status]}>
                        {t(`invoices.statuses.${inv.status}`)}
                      </Badge>
                      {inv.demo ? (
                        <Badge tone="orange" dot={false}>
                          {t('invoices.demo')}
                        </Badge>
                      ) : null}
                    </span>
                  </td>
                  <td className="py-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t('invoices.download', { number: inv.number })}
                      onClick={() =>
                        void openAuthedFile(`/billing/invoices/${inv.id}/pdf`).catch(() =>
                          toast.error(t('invoices.pdfFailed')),
                        )
                      }
                    >
                      <Download aria-hidden />
                      {t('invoices.pdf')}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// כלי הדגמה (ספק מדומה בלבד)
// ---------------------------------------------------------------------------

function DemoTools() {
  const { t } = useTranslation('billing');
  const run = useBillingAction();
  const simulate = useMutation({
    mutationFn: (outcome: 'success' | 'failure') =>
      run(
        () => request('/billing/simulate/renewal', { method: 'POST', body: { outcome } }),
        t('demo.done'),
      ),
  });
  return (
    <Card className="border-dashed">
      <CardHeader className="flex-row items-start gap-3">
        <FlaskConical className="mt-0.5 size-4 shrink-0 text-fg-muted" aria-hidden />
        <div>
          <CardTitle>{t('demo.title')}</CardTitle>
          <CardDescription>{t('demo.description')}</CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={simulate.isPending}
          onClick={() => simulate.mutate('success')}
        >
          {t('demo.success')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={simulate.isPending}
          onClick={() => simulate.mutate('failure')}
        >
          <AlertTriangle aria-hidden />
          {t('demo.failure')}
        </Button>
      </CardContent>
    </Card>
  );
}
